"use client";

import { useState, useRef, useEffect } from "react";

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  regionOptions,
  regionLabel = "Region",
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  // Optional quick-select: clicking the region button replaces the
  // current selection with this fixed list, configured on Settings >
  // Region states. Omitted entirely if there's no region set yet.
  regionOptions?: string[];
  regionLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [panelTop, setPanelTop] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Measured, not guessed: panel opens just below THIS filter row
  // (the button's own container), not a hardcoded pixel constant that
  // would silently drift if the row's height/content changes later.
  const openPanel = () => {
    if (ref.current) {
      const filterRow = ref.current.closest("[data-filter-row]") ?? ref.current.parentElement;
      const rect = (filterRow ?? ref.current).getBoundingClientRect();
      // Clamp so the panel never opens below the fold regardless of
      // how far down the page the filter row currently sits (e.g. if
      // the user scrolled past it) -- leaves at least ~40vh for the
      // panel itself to render into.
      const maxTop = window.innerHeight * 0.3;
      setPanelTop(Math.min(rect.bottom + 8, maxTop));
    }
    setOpen((o) => !o);
  };

  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  }

  const buttonLabel =
    selected.length === 0 ? label : selected.length === 1 ? selected[0] : `${label} (${selected.length})`;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={openPanel}
        className="h-9 rounded-md border bg-[var(--color-surface-0)] px-3 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-accent)]"
        style={{
          borderColor: selected.length > 0 ? "var(--color-accent)" : "var(--color-border)",
          backgroundColor: "var(--color-surface-1)",
        }}
      >
        {buttonLabel}
      </button>
      {open && (
        <>
          {/* Fixed panel, pinned just below the filter row -- NOT
              centered over the whole page (was disconnected from the
              button, confusing) and NOT anchored to the individual
              button's left/right edge (fragile: button width/position
              shifts with selection count, so the same anchor edge
              overflows the viewport depending on where the button
              lands in the row -- confirmed both directions in earlier
              iterations). Pinning below the row itself, full width of
              the page's content column, is stable regardless of which
              filter button opened it or how wide that button is. */}
          <div className="fixed inset-0 z-20 bg-black/30" onClick={() => setOpen(false)} />
          <div
            className="fixed left-1/2 z-20 max-h-[70vh] w-[calc(100vw-2rem)] max-w-3xl -translate-x-1/2 overflow-y-auto rounded-md border p-3 shadow-lg"
            style={{
              top: panelTop,
              borderColor: "var(--color-border)",
              backgroundColor: "var(--color-surface-2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <MultiSelectPanelContent
              options={options}
              selected={selected}
              onChange={onChange}
              toggle={toggle}
              regionOptions={regionOptions}
              regionLabel={regionLabel}
            />
          </div>
        </>
      )}
    </div>
  );
}

function MultiSelectPanelContent({
  options,
  selected,
  onChange,
  toggle,
  regionOptions,
  regionLabel,
}: {
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  toggle: (value: string) => void;
  regionOptions?: string[];
  regionLabel: string;
}) {
  const gridClass = "grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-5";

  return (
    <>
      {selected.length > 0 && (
        <button
          onClick={() => onChange([])}
          className="mb-2 w-full rounded px-2 py-1.5 text-left text-xs text-[var(--color-accent-strong)] hover:bg-[var(--color-surface-1)]"
        >
          Clear all
        </button>
      )}
      {regionOptions && regionOptions.length > 0 && (
        <button
          onClick={() => onChange(regionOptions)}
          className="mb-3 flex w-full items-center gap-1.5 rounded-md border px-2 py-2 text-left text-xs font-medium"
          style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}
        >
          {regionLabel}
        </button>
      )}
      <div className={gridClass}>
        {options.map((option) => (
          <label
            key={option}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded px-1.5 py-1.5 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-surface-1)]"
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => toggle(option)}
              className="accent-[var(--color-accent)]"
            />
            {option}
          </label>
        ))}
      </div>
      {options.length === 0 && (
        <div className="px-2 py-1.5 text-sm text-[var(--color-text-tertiary)]">No options</div>
      )}
    </>
  );
}
